import { Bell, Moon, Sun, Search, Menu, LogOut, Settings, User, Check, GraduationCap, Users, Navigation, Crown, Sparkles, LayoutGrid, Wrench, Sliders, Zap, Command } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { contentEngineApi } from '../../services/contentEngineApi';
import { searchGlobalRegistry } from '../../lib/globalSearchIndex';

export function Header() {
  const {
    currentUser,
    currentSchool,
    sidebarCollapsed,
    theme,
    toggleTheme,
    toggleMobileDrawer,
    logout,
    notifications,
    unreadCount,
    markNotificationRead,
    markAllNotificationsRead,
  } = useApp();

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Individual (B2C) teachers — no sidebar → header must span full width.
  const isIndividual = currentUser?.role === 'teacher' && !currentUser?.schoolId;
  // Below `lg` (1024px) the sidebar is an overlay drawer, so header always
  // starts at left:0. Above `lg` it offsets by the desktop rail width.
  const desktopOffset = isIndividual ? 0 : (sidebarCollapsed ? 80 : 256);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Tier badge for individual teachers — fetched once on mount + refreshed
  // whenever a subscription-state change event is broadcast by the app.
  const [tier, setTier] = useState(null);
  useEffect(() => {
    if (!isIndividual) return;
    let mounted = true;
    const load = async () => {
      try {
        const sub = await contentEngineApi.getMySubscription();
        if (mounted) setTier((sub?.tier || 'free').toLowerCase());
      } catch {
        if (mounted) setTier('free');
      }
    };
    load();
    const onSubChange = () => load();
    window.addEventListener('ce:subscription-changed', onSubChange);
    return () => {
      mounted = false;
      window.removeEventListener('ce:subscription-changed', onSubChange);
    };
  }, [isIndividual]);

  const openUpgrade = () => {
    if (window.location.pathname !== '/teacher-assignments') {
      navigate('/teacher-assignments?plans=1');
      return;
    }
    window.dispatchEvent(new CustomEvent('ce:upgrade-required', { detail: { source: 'header' } }));
  };
  const debounceRef = useRef(null);

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      setShowResults(false);
      return;
    }
    setIsSearching(true);
    try {
      // Backend (students/teachers/pages) + client-side registry (cards/settings/tools/actions)
      const role = currentUser?.role || 'teacher';
      const localGroups = searchGlobalRegistry(query, role);
      let backend = { students: [], teachers: [], pages: [] };
      try { backend = await api.search(query); } catch { /* ignore */ }
      setSearchResults({ ...backend, ...localGroups });
      setShowResults(true);
      setActiveIndex(0);
    } finally {
      setIsSearching(false);
    }
  }, [currentUser?.role]);

  // Iter 77b — BOSCO Studio access check (Elite Individual only).
  const [boscoAccess, setBoscoAccess] = useState({ studioAvailable: false });
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.role !== 'teacher' || currentUser.schoolId) return;
    const token = localStorage.getItem('sip_token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bosco/access`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { studioAvailable: false }))
      .then(setBoscoAccess)
      .catch(() => {});
  }, [currentUser?.userId]);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  const handleResultClick = (item) => {
    setShowResults(false);
    setSearchQuery('');
    setActiveIndex(-1);
    // Action items — fire events
    if (item.event) {
      if (item.event === 'app:toggle-theme') { toggleTheme(); return; }
      if (item.event === 'app:logout') { logout(); return; }
      window.dispatchEvent(new CustomEvent(item.event, { detail: item.eventDetail || {} }));
      return;
    }
    // Navigate (and scroll to hash if present)
    if (item.type === 'student') { navigate(`/students/${item.studentId}`); return; }
    if (item.type === 'teacher') { navigate(`/teachers/${item.userId}`); return; }
    const path = item.path || '';
    navigate(path);
    if (path.includes('#')) {
      const anchor = path.split('#')[1];
      // Wait for route render, then scroll
      setTimeout(() => {
        const el = document.getElementById(anchor) || document.querySelector(`[data-testid="${anchor}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-amber-400');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1800);
        }
      }, 350);
    }
  };

  // Flatten results in display order for keyboard navigation
  const flatResults = useMemo(() => {
    if (!searchResults) return [];
    const r = searchResults;
    return [
      ...(r.tools || []),
      ...(r.actions || []),
      ...(r.cards || []),
      ...(r.settings || []),
      ...(r.pages || []),
      ...(r.students || []),
      ...(r.teachers || []),
    ];
  }, [searchResults]);

  const onSearchKeyDown = (e) => {
    if (!showResults) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flatResults[activeIndex];
      if (item) handleResultClick(item);
    } else if (e.key === 'Escape') {
      setShowResults(false);
      inputRef.current?.blur();
    }
  };

  // Global Cmd/Ctrl+K shortcut to focus the search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Prefer the desktop input; fall back to opening mobile drawer.
        if (window.innerWidth >= 768) {
          inputRef.current?.focus();
        } else {
          setMobileSearchOpen(true);
          setTimeout(() => mobileInputRef.current?.focus(), 50);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userInitials = currentUser?.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2) || 'U';

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-500/10 text-purple-600';
      case 'admin': return 'bg-brand-deep-blue/10 text-brand-deep-blue';
      case 'teacher': return 'bg-emerald-500/10 text-emerald-600';
      case 'student': return 'bg-brand-light-blue/10 text-brand-light-blue';
      case 'parent': return 'bg-amber-500/10 text-amber-600';
      case 'finance_admin': return 'bg-pink-500/10 text-pink-600';
      default: return 'bg-slate-500/10 text-slate-600';
    }
  };

  const formatRoleName = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'User';
  };

  return (
    <header
      className={cn(
        'fixed top-0 right-0 left-0 h-14 sm:h-16 z-30',
        'backdrop-blur-xl bg-white/90 dark:bg-ae-navy/90',
        'border-b border-slate-200/70 dark:border-slate-800/60 shadow-[0_1px_0_rgba(11,18,36,0.02)]',
        'flex items-center justify-between px-3 sm:px-6 gap-2',
        'transition-[left,padding-left] duration-300 ease-in-out'
      )}
      style={{
        left: isDesktop ? desktopOffset : 0,
      }}
      data-testid="header"
    >
      {/* Left Section */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
        {!isIndividual && (
          <button
            onClick={toggleMobileDrawer}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 -ml-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        
        {/* Search — visible on md+, replaced by a search icon on mobile */}
        <div className="hidden md:flex items-center relative" ref={searchRef}>
          <div className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-xl bg-ae-bg dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 w-64 lg:w-80 ring-0 focus-within:ring-2 focus-within:ring-ae-blue/30 focus-within:border-ae-blue transition-[box-shadow,border-color]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search cards, settings, tools…"
              className="bg-transparent border-none outline-none text-sm flex-1 placeholder:text-muted-foreground min-w-0"
              value={searchQuery}
              onChange={onSearchChange}
              onKeyDown={onSearchKeyDown}
              onFocus={() => searchResults && setShowResults(true)}
              data-testid="search-input"
            />
            {isSearching ? (
              <div className="w-4 h-4 border-2 border-brand-deep-blue border-t-transparent rounded-full animate-spin" />
            ) : (
              <kbd
                data-testid="search-kbd-hint"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-[10px] font-mono text-muted-foreground bg-white/70 dark:bg-slate-900/70 shrink-0"
                title="Press Ctrl/⌘+K to focus search"
              >
                <Command className="w-3 h-3" />K
              </kbd>
            )}
          </div>
          {showResults && searchResults && (
            <div className="absolute top-full left-0 mt-1 w-[28rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-50 max-h-[28rem] overflow-y-auto" data-testid="search-results-dropdown">
              {(() => {
                const r = searchResults;
                // Synchronous total — avoids lazy mutation bug in nested JSX.
                const total =
                  (r.tools?.length || 0) +
                  (r.actions?.length || 0) +
                  (r.cards?.length || 0) +
                  (r.settings?.length || 0) +
                  (r.pages?.length || 0) +
                  (r.students?.length || 0) +
                  (r.teachers?.length || 0);
                let cursor = 0;
                const Section = ({ title, items, icon: Icon, tint, render, testidPrefix, stripPrefix }) => {
                  if (!items?.length) return null;
                  const startIdx = cursor;
                  cursor += items.length;
                  return (
                    <div>
                      <div className="px-3 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider border-t first:border-t-0 dark:border-slate-700 flex items-center gap-1.5">
                        {Icon && <Icon className="w-3 h-3" />} {title}
                      </div>
                      {items.map((it, i) => {
                        const idx = startIdx + i;
                        const active = idx === activeIndex;
                        // Strip redundant 'tool-'/'card-'/'setting-'/'action-' prefix from
                        // registry ids so the final testid stays clean (no double prefix).
                        let rawId = (it.id || it.studentId || it.userId || it.name || '')
                          .toString().toLowerCase().replace(/\s+/g, '-');
                        if (stripPrefix && rawId.startsWith(stripPrefix)) {
                          rawId = rawId.slice(stripPrefix.length);
                        }
                        return (
                          <button
                            key={it.id || it.studentId || it.userId || it.path || `${title}-${i}`}
                            onClick={() => handleResultClick(it)}
                            onMouseEnter={() => setActiveIndex(idx)}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left',
                              active ? 'bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                            )}
                            data-testid={`${testidPrefix}-${rawId}`}
                          >
                            <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', tint)}>
                              {Icon && <Icon className="w-4 h-4" />}
                            </div>
                            {render(it)}
                          </button>
                        );
                      })}
                    </div>
                  );
                };
                return (
                  <>
                    <Section
                      title="Tools"
                      items={r.tools}
                      icon={Wrench}
                      tint="bg-violet-100 dark:bg-violet-900/30 text-violet-600"
                      testidPrefix="search-result-tool"
                      stripPrefix="tool-"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{it.description}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Quick Actions"
                      items={r.actions}
                      icon={Zap}
                      tint="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600"
                      testidPrefix="search-result-action"
                      stripPrefix="action-"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{it.description}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Cards"
                      items={r.cards}
                      icon={LayoutGrid}
                      tint="bg-sky-100 dark:bg-sky-900/30 text-sky-600"
                      testidPrefix="search-result-card"
                      stripPrefix="card-"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{it.description}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Settings"
                      items={r.settings}
                      icon={Sliders}
                      tint="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                      testidPrefix="search-result-setting"
                      stripPrefix="setting-"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{it.description}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Pages"
                      items={r.pages}
                      icon={Navigation}
                      tint="bg-purple-100 dark:bg-purple-900/30 text-purple-600"
                      testidPrefix="search-result-page"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{it.description}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Students"
                      items={r.students}
                      icon={GraduationCap}
                      tint="bg-blue-100 dark:bg-blue-900/30 text-blue-600"
                      testidPrefix="search-result-student"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.fullName}</p>
                          <p className="text-xs text-muted-foreground">Class {it.class}-{it.section}</p>
                        </div>
                      )}
                    />
                    <Section
                      title="Teachers"
                      items={r.teachers}
                      icon={Users}
                      tint="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600"
                      testidPrefix="search-result-teacher"
                      render={(it) => (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground">{it.email}</p>
                        </div>
                      )}
                    />
                    {total === 0 && (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">No results found for "{searchQuery}"</div>
                    )}
                    <div className="border-t dark:border-slate-700 px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between bg-slate-50/60 dark:bg-slate-800/40">
                      <span>↑↓ Navigate • ↵ Open • Esc Close</span>
                      <span data-testid="search-results-count">{total} result{total === 1 ? '' : 's'}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1 sm:gap-3 shrink-0">
        {/* Individual-teacher tier badge + Upgrade CTA */}
        {isIndividual && tier && (() => {
          const TIER_META = {
            free:    { label: 'Free',    cls: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700' },
            starter: { label: 'Starter', cls: 'bg-sky-50 text-sky-800 border-sky-300 dark:bg-sky-900/30 dark:text-sky-300' },
            pro:     { label: 'Pro',     cls: 'bg-violet-50 text-violet-800 border-violet-300 dark:bg-violet-900/30 dark:text-violet-300' },
            elite:   { label: 'Elite',   cls: 'bg-gradient-to-r from-amber-200 to-amber-300 text-amber-900 border-amber-400' },
          };
          const meta = TIER_META[tier] || TIER_META.free;
          const canUpgrade = tier !== 'elite';
          return (
            <div className="flex items-center gap-1.5 sm:gap-2" data-testid="header-tier-cluster">
              <button
                onClick={openUpgrade}
                className={cn(
                  'inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 h-7 rounded-full border text-[10px] sm:text-[11px] font-bold uppercase tracking-wider transition-all hover:shadow-sm',
                  meta.cls
                )}
                data-testid="header-tier-badge"
                title="View plan"
              >
                <Crown className="h-3 w-3" />
                {meta.label}
              </button>
              {canUpgrade && (
                <Button
                  size="sm"
                  onClick={openUpgrade}
                  className="h-7 px-2 sm:px-3 text-xs bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-sm hover:shadow-md transition-all"
                  data-testid="header-upgrade-btn"
                  title="Upgrade your plan"
                >
                  <Sparkles className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Upgrade</span>
                </Button>
              )}
            </div>
          );
        })()}

        {/* Mobile Search trigger — opens an inline search overlay below header */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setMobileSearchOpen((v) => !v);
            setTimeout(() => mobileInputRef.current?.focus(), 60);
          }}
          data-testid="mobile-search-trigger"
          className="rounded-lg md:hidden h-9 w-9"
          title="Search"
        >
          <Search className="w-5 h-5" />
        </Button>

        {/* Theme Toggle — hidden on extra-small to save real-estate */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="rounded-lg hidden xs:inline-flex sm:inline-flex h-9 w-9"
        >
          {theme === 'light' ? (
            <Moon className="w-5 h-5" />
          ) : (
            <Sun className="w-5 h-5" />
          )}
        </Button>

        {/* BOSCO Studio toggle — Elite Individual only (iter 77b) */}
        {boscoAccess.studioAvailable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/bosco-studio')}
            data-testid="header-bosco-toggle"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-black text-emerald-300 hover:bg-emerald-950 hover:text-emerald-200 font-mono px-2.5 h-9"
            title="Switch to BOSCO Studio™ Expert Mode"
          >
            <span className="text-emerald-500 font-bold">❯_</span>
            <span className="text-xs">BOSCO</span>
          </Button>
        )}

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-lg"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-brand-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllNotificationsRead}
                  className="h-auto py-1 px-2 text-xs"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 ? (
              <div className="py-6 text-center text-muted-foreground text-sm">
                No notifications
              </div>
            ) : (
              notifications.slice(0, 5).map((notification) => (
                <DropdownMenuItem
                  key={notification.notificationId}
                  className={cn(
                    'flex flex-col items-start gap-1 py-3 cursor-pointer',
                    !notification.readStatus && 'bg-brand-light-blue/5'
                  )}
                  onClick={() => !notification.readStatus && markNotificationRead(notification.notificationId)}
                >
                  <span className="font-medium text-sm">{notification.type.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-muted-foreground line-clamp-2">
                    {notification.message}
                  </span>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2"
              data-testid="user-menu"
            >
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-brand-deep-blue text-white text-sm font-medium">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{currentUser?.name}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{currentUser?.name}</span>
                <span className="text-xs text-muted-foreground font-normal">{currentUser?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/my-profile')} data-testid="user-menu-profile">
              <User className="w-4 h-4 mr-2" />
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/preferences')} data-testid="user-menu-preferences">
              <Settings className="w-4 h-4 mr-2" />
              Preferences
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile inline search overlay — slides under the header on small screens */}
      {mobileSearchOpen && (
        <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5" data-testid="mobile-search-overlay">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 ring-1 ring-amber-400/60">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              ref={mobileInputRef}
              type="text"
              placeholder="Search cards, settings, tools…"
              className="bg-transparent border-none outline-none text-sm flex-1 placeholder:text-muted-foreground min-w-0"
              value={searchQuery}
              onChange={onSearchChange}
              onKeyDown={(e) => { if (e.key === 'Escape') { setMobileSearchOpen(false); } onSearchKeyDown(e); }}
              data-testid="mobile-search-input"
            />
            {isSearching && (
              <div className="w-4 h-4 border-2 border-brand-deep-blue border-t-transparent rounded-full animate-spin" />
            )}
            <button
              type="button"
              onClick={() => setMobileSearchOpen(false)}
              className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              data-testid="mobile-search-close"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
